import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  albumResource,
  type AlbumRef,
  type CreateAlbumInput,
  type RenameAlbumInput,
} from './album.resource';

export const albumsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: ['organizations', organizationId, 'albums'],
    queryFn: () => albumResource.list(organizationId),
  });

export function useAlbums(organizationId: string | null) {
  return useQuery({
    ...albumsQuery(organizationId ?? ''),
    enabled: Boolean(organizationId),
    select: (data) => data.albums,
  });
}

export function useAlbum(organizationId: string, albumId: string) {
  return useQuery({
    ...albumsQuery(organizationId),
    select: (data) => data.albums.find((album) => album.id === albumId),
  });
}

function useRefresh() {
  const client = useQueryClient();
  return async (organizationId: string) => {
    await client.invalidateQueries({
      queryKey: ['organizations', organizationId, 'albums'],
    });
    await client.invalidateQueries({
      queryKey: ['organizations', organizationId, 'photos'],
    });
  };
}

export function useCreateAlbum() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: CreateAlbumInput) => albumResource.create(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useRenameAlbum() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: RenameAlbumInput) => albumResource.rename(input),
    onSuccess: (_data, input) => refresh(input.organizationId),
  });
}

export function useDeleteAlbum() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (ref: AlbumRef) => albumResource.remove(ref),
    onSuccess: (_data, ref) => refresh(ref.organizationId),
  });
}
